import { AggregateEnergyFlow } from "@inrange/building-manager-api-client/models-site";
import { BarDatum } from "@nivo/bar";
import React, { useRef } from "react";
import styled from "styled-components";
import { formatUnitsToNearestTen } from "../../formatting/formatKiloUnits";
import {
  ChartToolTip,
  ChartToolTipBody,
  ChartToolTipHeader,
} from "../ChartTooltip";
import BarChart from "./BarChart";
import { ChartData, getChartLegend, legendKeyColors } from "./chart-types";
import {
  Chart,
  ChartHeader,
  ChartLegend,
  ChartTooltipColorBox,
  ChartWrapper,
} from "./common";
import { NonOverflowTooltip } from "./NonOverflowTooltip";

const ToolTipData: React.FC<{
  chartLegend: Record<string, string>;
  data: BarDatum;
}> = ({ chartLegend, data }) => {
  return (
    <>
      {Object.keys(chartLegend).map((key, index) => (
        <ChartToolTipBody key={index}>
          <ChartTooltipColorBox color={legendKeyColors[key]} />
          {key}:{" "}
          <strong>
            {formatUnitsToNearestTen(
              typeof data[key] === "string" ? parseFloat(data[key]) : data[key],
              "kWh"
            )}
          </strong>
        </ChartToolTipBody>
      ))}
    </>
  );
};

const PeakOffPeakChart: React.FC<{
  chartData: ChartData[];
  energyFlowAnnual: AggregateEnergyFlow;
  isGeneratingSite: boolean;
  isBatteryArbitrageSite: boolean;
  height?: string;
}> = ({
  chartData,
  energyFlowAnnual,
  isGeneratingSite,
  isBatteryArbitrageSite,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const chartLegend = getChartLegend(chartData);
  const subHeader = isGeneratingSite ? (
    <>
      <ChartHeader>
        {formatUnitsToNearestTen(energyFlowAnnual.generation, "kWh")}
        <label>Total on-site generation in year 1</label>
      </ChartHeader>
      {isBatteryArbitrageSite && (
        <ChartHeader>
          {formatUnitsToNearestTen(
            energyFlowAnnual.behindMeterBatteryGrid,
            "kWh"
          )}
          <label>Peak shifted by battery in year 1</label>
        </ChartHeader>
      )}
    </>
  ) : null;

  const showPeakOffPeakTooltip = (e: { data: BarDatum }): JSX.Element => {
    const eventCoords = e as unknown as { x: number; y: number };
    return (
      <NonOverflowTooltip
        point={{ x: eventCoords.x, y: eventCoords.y }}
        containerSize={ref.current!.getBoundingClientRect()}
      >
        <ChartToolTip>
          <ChartToolTipHeader>{e.data.index}</ChartToolTipHeader>
          <ToolTipData chartLegend={chartLegend} data={e.data} />
        </ChartToolTip>
      </NonOverflowTooltip>
    );
  };

  return (
    <ChartWrapper ref={ref} height={props.height}>
      <div>
        <ChartHeader>Energy supply by hours and source</ChartHeader>
        <HeaderRow>{subHeader}</HeaderRow>
      </div>
      <Chart>
        <ChartLegend chartLegend={chartLegend} />
        <BarChart
          chartData={chartData as BarDatum[]}
          tooltipFn={showPeakOffPeakTooltip}
          keys={Object.keys(chartLegend)}
          barColors={Object.values(chartLegend)}
        />
      </Chart>
    </ChartWrapper>
  );
};

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export default PeakOffPeakChart;
